import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  type CreateSellerProfilePayload,
  sellerProfilesAPI,
  type UpdateSellerProfilePayload,
} from '../lib/api';
import type { SellerProfile } from '../types/leads';

export function useSellerProfiles(organizationId: string | undefined) {
  return useQuery<SellerProfile[]>({
    queryKey: ['seller-profiles', organizationId],
    queryFn: async () => {
      const { data } = await sellerProfilesAPI.getAll(organizationId!);
      return data;
    },
    enabled: !!organizationId,
  });
}

export function useCreateSellerProfile() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (payload: CreateSellerProfilePayload) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await sellerProfilesAPI.create(orgId, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-profiles', currentOrganization?.id] });
    },
  });
}

export function useUpdateSellerProfile() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateSellerProfilePayload }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data: response } = await sellerProfilesAPI.update(orgId, userId, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-profiles', currentOrganization?.id] });
    },
  });
}

export function useDeleteSellerProfile() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (userId: string) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      await sellerProfilesAPI.remove(orgId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seller-profiles', currentOrganization?.id] });
    },
  });
}
